import { createFileRoute } from "@tanstack/react-router";
import { SiteShell } from "@/components/site/SiteShell";
import { alternatePath, type Locale } from "@/lib/i18n";
import {
  breadcrumbJsonLd,
  faqJsonLd,
  webPageJsonLd,
} from "@/lib/seo";
import {
  buildCorporateHead,
  corporateProfessionalServiceJsonLd,
} from "@/lib/corporate-seo";
import { publicProjectsQuery, siteSettingsQuery } from "@/lib/query-registry";
import { setEdgeHtmlCache } from "@/lib/cache-headers";
import { CorporateHomeContent } from "@/components/home/CorporateHomeContent";
import { HOME_FAQS } from "@/components/home/home-faqs";
import type { HomeProject } from "@/components/home/types";

export const Route = createFileRoute("/")({
  head: () => {
    const path = "/";
    const locale: Locale = "en";
    const altPath = alternatePath(path, "es");
    return buildCorporateHead({
      locale,
      title: "Senior Shopify Engineering for Established DTC & B2B Brands | Shugert",
      description:
        "Senior Shopify engineering for established brands facing constraints in performance, conversion, technical SEO, migrations, integrations, Shopify Plus and ongoing delivery.",
      path,
      altPath,
      ogImage: "/og-default-v2.jpg",
      jsonLd: [
        corporateProfessionalServiceJsonLd("en"),
        breadcrumbJsonLd([{ name: "Home", path: "/" }]),
        faqJsonLd(HOME_FAQS.en),
        webPageJsonLd({
          name: "Senior Shopify Engineering for Established DTC and B2B Brands",
          path: "/",
          description:
            "Shugert helps established Shopify businesses remove technical and operational constraints across experience, visibility, platform and operations.",
          inLanguage: "en-US",
        }),
      ],
    });
  },
  loader: async ({ context }) => {
    await setEdgeHtmlCache();
    const [{ projects }, { settings }] = await Promise.all([
      context.queryClient.ensureQueryData(publicProjectsQuery()),
      context.queryClient.ensureQueryData(siteSettingsQuery()),
    ]);
    return {
      projects: projects as unknown as HomeProject[],
      homeLimit: settings.home_projects_limit,
    };
  },
  component: HomeRoute,
});

function HomeRoute() {
  const { projects, homeLimit } = Route.useLoaderData();
  return (
    <SiteShell>
      <div className="home-money-palette">
        <CorporateHomeContent projects={projects} homeLimit={homeLimit} />
      </div>
    </SiteShell>
  );
}
